import React, { useState } from 'react';
import { Search, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, DollarSign, Scale, FileText, Users, ChevronDown } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ServiceHeroForm from '../../components/ServiceHeroForm';
import RegistrationModal from '../../components/RegistrationModal';

const TrademarkSearchPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Avoid Legal Issues',
      description: 'Prevent costly trademark conflicts and potential lawsuits by ensuring your mark is available'
    },
    {
      icon: CheckCircle,
      title: 'Save Time & Money',
      description: 'Avoid the expense of rebranding if your trademark application gets rejected'
    },
    {
      icon: TrendingUp,
      title: 'Strategic Insights',
      description: 'Understand the competitive landscape and position your brand effectively'
    },
    {
      icon: Clock,
      title: 'Fast Results',
      description: 'Get comprehensive search results within 24-48 hours'
    }
  ];

  const whatWeSearch = [
    'Federal trademark database (USPTO)',
    'State trademark registrations (all 50 states)',
    'Common law trademarks',
    'Domain name registrations',
    'Social media handles',
    'Business name registrations',
    'Similar phonetic spellings',
    'Visual similarities in logos'
  ];

  const searchTypes = [
    {
      name: 'Knockout Search',
      price: '$99',
      description: 'Quick preliminary search to identify obvious conflicts',
      features: ['USPTO database search', 'Identical and near-identical matches', '24-hour turnaround', 'Basic risk assessment']
    },
    {
      name: 'Comprehensive Search',
      price: '$299',
      description: 'In-depth search across all databases (Included in Premium & Signature packages)',
      features: ['All federal and state databases', 'Common law search', 'Domain and social media', 'Phonetic and visual analysis', 'Detailed risk report', '48-hour turnaround']
    },
    {
      name: 'International Search',
      price: '$499',
      description: 'Global trademark clearance for international expansion',
      features: ['All comprehensive search features', 'Madrid Protocol countries', 'Country-specific databases', 'International trademark offices', 'Multi-language analysis']
    }
  ];

  const caseStudies = [
    {
      title: 'Tech Startup Avoids $200K Rebranding',
      scenario: 'A software startup was ready to launch with $150K invested in branding and marketing.',
      problem: 'Discovered a conflicting trademark only after receiving a cease & desist letter.',
      solution: 'Our comprehensive search would have identified the conflict before launch, saving complete rebranding costs.',
      outcome: 'This is why 98% of our clients who conduct searches avoid rejection.'
    },
    {
      title: 'E-commerce Brand Secures International Rights',
      scenario: 'An online retailer wanted to expand to Europe and Asia.',
      problem: 'Discovered their brand name was already trademarked in 3 target countries.',
      solution: 'Our international search revealed conflicts early, allowing them to modify their brand strategy.',
      outcome: 'Successfully launched in 12 countries with a slightly modified brand name.'
    }
  ];

  const timeline = [
    { time: '0-2 hours', activity: 'Submit search request with trademark details' },
    { time: '2-24 hours', activity: 'AI-powered database scanning across multiple sources' },
    { time: '24-48 hours', activity: 'Attorney review and risk assessment' },
    { time: '48 hours', activity: 'Receive comprehensive report with recommendations' }
  ];

  const faqs = [
    {
      question: 'How is a comprehensive trademark search different from a free USPTO search?',
      answer: 'A free USPTO search only shows identical matches in the federal database. Our comprehensive search includes state registrations, common law marks, phonetic similarities, visual similarities, domain names, social media handles, and business registrations. We also provide expert analysis of conflict likelihood, which the USPTO database does not offer.'
    },
    {
      question: 'What happens if you find a conflicting trademark?',
      answer: "We provide a detailed risk assessment explaining the likelihood of conflict, the strength of the existing mark, and your options. These may include: modifying your mark, filing in different classes, pursuing coexistence agreements, or choosing a different mark entirely. Our attorneys can guide you through the best strategy for your situation."
    },
    {
      question: 'Can I file a trademark without conducting a search?',
      answer: 'While not legally required, filing without a search is highly risky. USPTO filing fees ($225-$400 per class) are non-refundable, and rejection rates for applications without prior searches exceed 50%. Additionally, you risk investing in branding and marketing for a name you cannot legally protect.'
    },
    {
      question: 'How far back does the search go?',
      answer: 'Our search covers all active and pending trademarks regardless of filing date. We also check abandoned marks that may still have common law rights, recent business filings, domain registrations from the past 5 years, and social media accounts created within the last 3 years.'
    },
    {
      question: 'Do I need a search if I already own the domain name?',
      answer: 'Yes, absolutely. Domain ownership does not grant trademark rights. Someone else may have filed for trademark protection before you registered the domain. Our search ensures no one else has legal trademark rights that could prevent your registration or force you to stop using your domain.'
    },
    {
      question: 'What if the search shows my trademark is available?',
      answer: 'If our search shows no conflicts, we provide a clear recommendation to proceed with filing. We can immediately begin preparing your trademark application, selecting appropriate classes, and drafting descriptions to maximize approval chances. Our comprehensive package includes both search and filing.'
    }
  ];

  const commonMistakes = [
    { mistake: 'Only checking exact matches', impact: 'USPTO considers similar marks confusing, not just identical ones' },
    { mistake: 'Ignoring state and common law marks', impact: 'These can block your federal registration and geographic expansion' },
    { mistake: 'Not checking phonetic similarities', impact: 'Sound-alike marks often cause confusion and rejection' },
    { mistake: 'Overlooking design similarities', impact: 'Visual elements matter as much as words in trademark law' },
    { mistake: 'Skipping international searches', impact: 'Blocks future global expansion opportunities' }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="max-w-3xl">
              <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Trademark Search & Clearance
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Comprehensive Trademark <span className="text-[#168356]">Search Services</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Avoid costly mistakes and rejection. Our AI-powered search technology scans millions of records to ensure your trademark is available before you file.
              </p>
              <div className="flex gap-4">
                <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">
                  Start Your Search Now
                </button>
                <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                  View Sample Report
                </button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#168356]" />
                  98% Success Rate
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#168356]" />
                  48-Hour Delivery
                </span>
                <span className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#168356]" />
                  Attorney Review
                </span>
              </div>
            </div>
          </div>
          <ServiceHeroForm title="Start Your Trademark Search" buttonText="Start Search" />
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">50%+</div>
              <div className="text-gray-600 text-sm">Applications rejected without search</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">$400+</div>
              <div className="text-gray-600 text-sm">Lost in USPTO fees alone</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">$100K+</div>
              <div className="text-gray-600 text-sm">Average rebranding cost</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">98%</div>
              <div className="text-gray-600 text-sm">Our approval rate with search</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#168356]/10 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is Trademark Search?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">Protect Your Brand Before You Invest</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                A trademark search is a crucial first step before filing your trademark application. It involves conducting a thorough investigation to determine if your proposed trademark conflicts with existing registered or pending trademarks.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Our AI-powered search technology scans millions of records across federal, state, and common law databases to identify potential conflicts. This comprehensive analysis helps you understand the risk level of your trademark and make informed decisions about your brand protection strategy.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Unlike free database searches that only show exact matches, our comprehensive search identifies phonetically similar marks, visually similar logos, marks in related industries, and even common law trademarks that may not be registered but still have legal protection.
              </p>
              <p className="text-lg font-semibold text-black">
                Don't risk spending thousands on branding and marketing only to discover someone else owns your trademark. Our search service provides peace of mind and a clear path forward.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-black mb-6">What We Search:</h3>
              <ul className="space-y-3">
                {whatWeSearch.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 italic">
                  "Our multi-database search technology checks over 50 million trademark records worldwide"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Why Trademark Search is Critical</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Skipping a trademark search can lead to devastating consequences for your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border-l-4 border-red-500 shadow-lg">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">Without a Search</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>50%+ rejection rate:</strong> USPTO rejects applications with conflicting marks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>$400+ lost fees:</strong> Non-refundable USPTO filing fees per class</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>$100K+ rebranding:</strong> Average cost to rebrand including marketing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Legal exposure:</strong> Risk of trademark infringement lawsuits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Lost time:</strong> 12-18 months wasted on rejected applications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Customer confusion:</strong> Damage to brand reputation and trust</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 border-l-4 border-[#168356] shadow-lg">
              <CheckCircle className="w-12 h-12 text-[#168356] mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">With Our Search</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>98% success rate:</strong> Identify conflicts before filing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Smart investment:</strong> $299 search vs $100K+ rebranding</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Informed decisions:</strong> Understand risks before investing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Legal protection:</strong> Build on solid legal foundation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Time savings:</strong> Fast-track to approval in 8-12 months</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Peace of mind:</strong> Confident brand building and marketing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Search Types & Pricing */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Choose Your Search Level</h2>
            <p className="text-lg text-gray-600">Select the search depth that matches your needs and budget</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {searchTypes.map((type, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${index === 1 ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {index === 1 && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Recommended
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{type.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{type.price}</div>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <ul className="space-y-3 mb-6">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${index === 1 ? 'bg-[#d29b00] text-white hover:bg-[#a87b00]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Real-World Impact</h2>
            <p className="text-lg text-gray-600">See how trademark searches saved these businesses</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#168356] uppercase mb-2">Our Solution</div>
                    <p className="text-gray-700">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Common Search Mistakes to Avoid</h2>
            <p className="text-lg text-gray-600">Don't let these errors derail your trademark application</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonMistakes.map((item, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-xl p-6">
                <AlertTriangle className="w-8 h-8 text-red-500 mb-4" />
                <h3 className="text-lg font-bold text-black mb-2">{item.mistake}</h3>
                <p className="text-gray-700 text-sm">{item.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Search Timeline</h2>
            <p className="text-lg text-gray-600">Get your results in 48 hours or less</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of Our Trademark Search</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default TrademarkSearchPage;